import { makeAutoObservable, runInAction } from "mobx";
import { downloadConnectorZip, isOnMyMachineServer } from "./connector";
import type RootStore from "stores/RootStore";
import { loadErrorMessage } from "shared/loadOrError";
import { isAdmin } from "auth/tier";
import { canUseDataMapper } from "../rbac";
import { LatestIntentQueue } from "../shared/latestIntent";
import {
  dataMapperSelection,
  dataMapperService,
  dataPlaneService,
  rawSourceConfigsService,
} from "../dataMapperServices";
import {
  DATA_MAPPER_SOURCE_TYPES,
  isFileSourceType,
  isRecordChanged,
  refusalReason,
  sameId,
  visibleToSelection,
  type DataMapperConnectorDownload,
  type DataMapperConnectorStatus,
  type DataMapperFileForm,
  type DataMapperMssqlForm,
  type DataMapperProfile,
  type DataMapperRestForm,
  type DataMapperRestSecrets,
  type DataMapperScope,
  type DataMapperSelectionQuery,
  type DataMapperSourceConfig,
  type DataMapperSourceType,
} from "../dataMapper.types";
import {
  FILE_EXTENSIONS,
  checkSourceFile,
  dataSourceErrorMessage,
  restConfigWithSecrets,
  sanitizeRestUrl,
  uploadSourceFile,
  type UploadHandle,
} from "./sourceRequests";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

export { dataSourceErrorMessage };

/** The Browse route the Data Source screen hands on to. */
export const DATA_MAPPER_BROWSE_ROUTE = "Data Mapper Browse";
/** The Explore route (schema, profiles, analysis, reports). */
export const DATA_MAPPER_EXPLORE_ROUTE = "Data Mapper Explore";

/** The API's refusals that mean the stored selection no longer exists (as the Library). */
const GONE_REASONS = new Set(["profile-not-found", "profile-not-in-client", "client-not-found"]);

const EMPTY_FORM: DataMapperMssqlForm = {
  name: "",
  server: "",
  port: "",
  database: "",
  authType: "sql",
  username: "",
  domain: "",
  encrypt: true,
  trustServerCertificate: false,
};

const EMPTY_FILE_FORM: DataMapperFileForm = {
  name: "",
  fileId: null,
  originalName: null,
  fileSize: null,
  delimiter: ",",
  hasHeader: true,
  encoding: "utf-8",
  rootPath: "",
  sheets: "",
  tableName: "",
};

const EMPTY_REST_FORM: DataMapperRestForm = {
  name: "",
  url: "",
  method: "GET",
  authType: "none",
  username: "",
  apiKeyHeader: "",
  responsePath: "",
  paginationType: "none",
  pageSize: "100",
  maxPages: "10",
  limitParam: "",
  offsetParam: "",
  pageParam: "",
  cursorParam: "",
  cursorPath: "",
};

/** The sentence a 409 record-changed on a saved connection shows. */
export const sourceConfigChangedNotice = (name: string): string =>
  `Someone else saved "${name}" after you loaded it, so your changes were not saved. ` +
  "The saved connections have been reloaded; load it again to apply yours.";

/** At most this many byte-pages of database names are followed (each is a fresh dial). */
const MAX_DATABASE_PAGES = 20;

/**
 * What a call carries that no store keeps: an MSSQL password, or a REST
 * source's token / password / API-key value, headers and body.
 */
export type DialSecret = string | DataMapperRestSecrets;

/** A config body with — only here, for this one body — its secret added. */
const withSecret = (config: Record<string, unknown>, secret: DialSecret): Record<string, unknown> => {
  if (typeof secret !== "string") return restConfigWithSecrets(config, secret);
  return secret ? { ...config, password: secret } : { ...config };
};

/**
 * A dial's request body: the selection, the config and — only here, for this
 * one body — the secret. A plain function, not a store method, so MobX never
 * sees the secret as an action argument. The Target screen's dials use it too.
 */
export function dialRequest(
  action: "test" | "connect" | "list-databases",
  query: DataMapperSelectionQuery & { tabId: string },
  config: Record<string, unknown>,
  secret: DialSecret
): Record<string, unknown> {
  return { action, ...query, config: withSecret(config, secret) };
}

const str = (v: unknown): string => (typeof v === "string" ? v : typeof v === "number" ? String(v) : "");
const trimmed = (v: string): string | undefined => (v.trim() ? v.trim() : undefined);
const positiveInt = (v: string): number | undefined => {
  const n = Number(v);
  return v.trim() !== "" && Number.isInteger(n) && n >= 1 ? n : undefined;
};
const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return "";
  }
};

/** The type a saved config fills the form as (its `config.type`: the row's `type` folds files into "file"). */
const savedType = (c: DataMapperSourceConfig): DataMapperSourceType | null => {
  const t = c.config.type ?? c.type;
  return DATA_MAPPER_SOURCE_TYPES.some((d) => d.type === t) ? (t as DataMapperSourceType) : null;
};

type Busy = "test" | "connect" | "disconnect" | "save" | "databases" | "upload";

/**
 * Data Mapper → Data Source: pick a saved source configuration or fill one in
 * — an MSSQL connection, an uploaded CSV / Excel / JSON file, or a REST API —
 * test it, connect this tab to it, and save it (without its secrets). Ported
 * from the standalone tool's DataSourcePanel / DataSourceStore.
 *
 * SECRETS. The MSSQL password and a REST source's token, password, API-key
 * value, headers and body are NEVER fields of this store, of a saved config or
 * of anything persisted. The form component holds them in its own state and
 * passes them to `test` / `connect` / `saveConfig`, which put them into the one
 * call body and nowhere else; the component clears them when the call settles.
 * Those calls go through the RAW Feathers client (`dataPlaneService`,
 * `rawSourceConfigsService`), not a ServiceStore, so no cache keeps the body;
 * and those methods are not MobX actions, so no spy() listener sees their
 * arguments. Saving an MSSQL or file config carries no secret and goes through
 * the ServiceStore as before.
 *
 * Saved configs are a computed view over the `data-mapper-source-configs`
 * ServiceStore; this store keeps only the chosen config's id and the forms
 * (UI state, like DataMapperProfilesStore's edit form). The connection state
 * is the API's `status` answer for this tab and selection.
 */
class DataMapperSourceStore {
  rootStore: RootStore;

  sourceType: DataMapperSourceType = "mssql";

  form: DataMapperMssqlForm = { ...EMPTY_FORM };
  fileForm: DataMapperFileForm = { ...EMPTY_FILE_FORM };
  restForm: DataMapperRestForm = { ...EMPTY_REST_FORM };
  /** The saved config the form was loaded from, if any (an id; the row stays in its ServiceStore). */
  selectedConfigId: string | null = null;
  /** Its `updatedAt` when loaded — sent back as `expectUpdatedAt` on save. */
  selectedConfigUpdatedAt: string | null = null;
  saveScope: DataMapperScope = "profile";
  /** The profile the form was filled under (an id). */
  private formProfileId: string | null = null;

  // Connection state for this tab + selection (the API's `status` answer)
  connected = false;
  connectedName: string | null = null;
  statusLoaded = false;

  busy: Busy | null = null;
  /** The last test's outcome, as a sentence. */
  testResult: string | null = null;
  error: string | null = null;
  notice: string | null = null;
  /** The last save's `droppedSecrets` (field paths, never values): shown so the person knows what to type again. */
  droppedSecrets: string[] = [];
  configsError: string | null = null;
  configsLoading = false;

  /** The running upload's progress, 0-100 (null when none is running). */
  uploadProgress: number | null = null;
  /** Why the file was not uploaded (a pre-check or the API's refusal). */
  uploadError: string | null = null;
  /** The running upload, to abort on route exit. Not observable. */
  private upload: UploadHandle | null = null;

  /** Admin only: the server's databases (names only), for the picker. */
  databases: string[] = [];

  /** The person's own HQ Connector and the downloads HQ serves; null until first read. */
  connector: DataMapperConnectorStatus | null = null;
  /** Why the connector status or a download failed. */
  connectorError: string | null = null;
  /** The platform being downloaded, while it is. */
  connectorDownloading: string | null = null;
  /** The status refresh while this screen is open. Not observable. */
  private connectorTimer: ReturnType<typeof setInterval> | null = null;

  private loadSeq = 0;
  /**
   * Bumped on route exit and on a profile change: a test / connect / save
   * that answers after either writes nothing here (Codex r1).
   */
  private opEpoch = 0;
  /** connect / disconnect: one at a time, the latest intent wins (shared/latestIntent.ts). */
  private readonly intents = new LatestIntentQueue();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    // The methods that take a secret are NOT actions: MobX reports an action's
    // arguments to spy() listeners (devtools, loggers), which would put the
    // password, token or headers in their event stream (Codex r1). Their state
    // writes go through runInAction or other actions, whose arguments carry no secret.
    makeAutoObservable<this, "upload" | "connectorTimer">(this, {
      rootStore: false,
      upload: false,
      connectorTimer: false,
      test: false,
      connect: false,
      listDatabases: false,
      saveConfig: false,
    });
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  private get configsService() {
    return dataMapperService(this.rootStore, "data-mapper-source-configs");
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get isAdmin(): boolean {
    return isAdmin(this.rootStore);
  }

  // ---- The HQ Connector ------------------------------------------------------

  /** The SQL Server form names this computer: HQ will reach it through the person's connector. */
  get serverOnMyMachine(): boolean {
    return this.sourceType === "mssql" && isOnMyMachineServer(this.form.server);
  }

  /** Windows login can be used: the person's connector is connected and can sign in as Windows. */
  get windowsLoginAvailable(): boolean {
    return this.connector?.connected === true && (this.connector.client?.sqlAuth ?? []).includes("integrated");
  }

  /** Read the person's connector status (and the downloads). Quiet: a failure is shown, not thrown. */
  async refreshConnector(): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-connector");
    if (!service || !this.canUse) return;
    try {
      const status = (await service.find()) as DataMapperConnectorStatus;
      runInAction(() => {
        this.connector = status;
        this.connectorError = null;
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.connectorError = loadErrorMessage(err, "HQ could not read your HQ Connector's status.");
      });
    }
  }

  /** While the screen is open and the server is this computer, keep the status current (10 s). */
  private startConnectorWatch() {
    this.stopConnectorWatch();
    void this.refreshConnector();
    this.connectorTimer = setInterval(() => {
      if (this.serverOnMyMachine) void this.refreshConnector();
    }, 10_000);
  }

  private stopConnectorWatch() {
    if (this.connectorTimer) clearInterval(this.connectorTimer);
    this.connectorTimer = null;
  }

  /** Download one connector zip with the person's own session. */
  async downloadConnector(download: DataMapperConnectorDownload): Promise<boolean> {
    if (this.connectorDownloading) return false;
    this.connectorDownloading = download.platform;
    this.connectorError = null;
    // A raw fetch the session guard never sees (shared/sessionDownload.ts).
    const ended = sessionMark(this.rootStore);
    try {
      const { env } = await import("../../../config/environment");
      // Before the token is read: after a sign-out it is the next person's.
      if (ended()) return false;
      const token = (this.rootStore as unknown as { authStore?: { token?: string | null } }).authStore?.token ?? null;
      return await downloadConnectorZip(env.apiUrl, token, download, ended);
    } catch (err) {
      if (isSessionEnded(err) || ended()) return false;
      runInAction(() => {
        this.connectorError = err instanceof Error ? err.message : "The download failed.";
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.connectorDownloading = null;
        });
      }
    }
  }

  get profileId(): string | null {
    return this.selection?.selectedProfileId ?? null;
  }

  get clientId(): string | null {
    return this.selection?.selectedClientId ?? null;
  }

  get profile(): DataMapperProfile | null {
    const id = this.profileId;
    if (!id) return null;
    const all = (dataMapperService(this.rootStore, "data-mapper-profiles")?.entities ??
      []) as unknown as DataMapperProfile[];
    return all.find((p) => sameId(p.id, id)) ?? null;
  }

  /** The saved configs this selection sees, narrowest scope first (the API's order). */
  get savedConfigs(): DataMapperSourceConfig[] {
    if (!this.profileId) return [];
    const rows = (this.configsService?.entities ?? []) as unknown as DataMapperSourceConfig[];
    return rows.filter((r) => visibleToSelection(r, this.clientId, this.profileId));
  }

  /** The saved configs of the chosen source type. */
  get savedConfigsOfType(): DataMapperSourceConfig[] {
    return this.savedConfigs.filter((c) => savedType(c) === this.sourceType);
  }

  get selectedConfig(): DataMapperSourceConfig | null {
    return this.savedConfigs.find((c) => sameId(c.id, this.selectedConfigId)) ?? null;
  }

  /** A loaded REST config whose saved form lost a credential: the person must type it again. */
  get restNeedsAuth(): boolean {
    const c = this.selectedConfig;
    return this.sourceType === "rest-api" && c?.config.hasAuth === true;
  }

  /**
   * The non-secret headers and body a loaded REST config kept (the API strips
   * the rest), for the form to put back into its own state.
   */
  get selectedRestKeptParts(): { headers: Array<{ name: string; value: string }>; body: string } {
    const c = this.selectedConfig?.config;
    const headers = c?.headers && typeof c.headers === "object" ? (c.headers as Record<string, unknown>) : {};
    return {
      headers: Object.entries(headers).map(([name, value]) => ({ name, value: str(value) })),
      body: str(c?.body),
    };
  }

  get canDial(): boolean {
    if (this.sourceType === "mssql") return this.form.server.trim() !== "" && this.form.database.trim() !== "";
    if (isFileSourceType(this.sourceType)) return this.fileForm.fileId !== null;
    return this.restForm.url.trim() !== "";
  }

  get canSave(): boolean {
    return this.canDial && this.busy === null;
  }

  /** The file types' accepted names, for the file input's `accept`. */
  get acceptedExtensions(): string {
    return isFileSourceType(this.sourceType) ? FILE_EXTENSIONS[this.sourceType].join(",") : "";
  }

  // ---- Route entry ----------------------------------------------------------

  /** Route entry: bind the selection, then load saved configs and this tab's status. */
  async enter(): Promise<void> {
    const authStore = (
      this.rootStore as unknown as { authStore?: { currentUser?: { id?: string | number } } }
    ).authStore;
    this.selection?.bindToUser(authStore?.currentUser?.id);
    // The forms belong to the profile they were filled under: another profile starts blank.
    if (!sameId(this.formProfileId, this.profileId)) {
      this.opEpoch++;
      this.intents.supersede();
      this.busy = null;
      this.databases = [];
      this.form = { ...EMPTY_FORM };
      this.fileForm = { ...EMPTY_FILE_FORM };
      this.restForm = { ...EMPTY_REST_FORM };
      this.selectedConfigId = null;
      this.selectedConfigUpdatedAt = null;
      this.saveScope = "profile";
      this.connected = false;
      this.connectedName = null;
      this.droppedSecrets = [];
      this.formProfileId = this.profileId;
    }
    this.startConnectorWatch();
    await this.load();
  }

  async load(): Promise<void> {
    const seq = ++this.loadSeq;
    const selection = this.selection;
    if (!this.canUse || !selection?.hasProfile) {
      this.configsLoading = false;
      return;
    }
    const query = { ...selection.selectionQuery };
    this.configsLoading = true;
    this.configsError = null;
    // The reset after a sign-out bumps loadSeq, but the epoch moves first: a
    // settle in between (one find failed on its own, the others were ended)
    // would still pass the seq check.
    const ended = sessionMark(this.rootStore);
    const results = await Promise.allSettled([
      this.configsService
        ? this.configsService.find({ query })
        : Promise.reject(new Error("The Data Mapper service is not available in this build.")),
      this.refreshStatus(),
      // So the header can name the profile after a reload.
      dataMapperService(this.rootStore, "data-mapper-profiles")?.find({ query: { clientId: query.clientId } }),
    ]);
    const failure = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
    runInAction(() => {
      if (seq !== this.loadSeq || ended()) return;
      this.configsLoading = false;
      if (!failure || isSessionEnded(failure.reason)) return;
      const err: unknown = failure.reason;
      if (GONE_REASONS.has(refusalReason(err) ?? "") && query.profileId) {
        selection.forgetProfile(query.profileId);
        this.configsError = "The profile that was open no longer exists. Choose one on the Profiles page.";
      } else {
        this.configsError = loadErrorMessage(err, "The saved connections could not be loaded.");
      }
    });
  }

  /** Ask the API whether this tab has a source open for this selection. */
  async refreshStatus(): Promise<void> {
    const selection = this.selection;
    const service = dataPlaneService(this.rootStore, "data-mapper-data-source");
    if (!selection?.hasProfile || !service) return;
    const seq = this.loadSeq;
    const ended = sessionMark(this.rootStore);
    const answer = (await service.create({ action: "status", ...selection.dataPlaneQuery })) as {
      connected?: boolean;
      name?: string | null;
    } | null;
    runInAction(() => {
      if (seq !== this.loadSeq || ended()) return;
      this.connected = answer?.connected === true;
      this.connectedName = this.connected ? (answer?.name ?? null) : null;
      this.statusLoaded = true;
    });
  }

  // ---- Forms -----------------------------------------------------------------

  /**
   * Choose the source type. Not while a call is in flight: its answer belongs
   * to the form as it was. A file uploaded for one file type is kept for
   * another only when its name fits that type too.
   */
  setSourceType(type: DataMapperSourceType) {
    if (this.busy) return;
    if (type === this.sourceType) return;
    this.sourceType = type;
    this.testResult = null;
    this.error = null;
    this.uploadError = null;
    this.droppedSecrets = [];
    this.selectedConfigId = null;
    this.selectedConfigUpdatedAt = null;
    const name = this.fileForm.originalName?.toLowerCase() ?? "";
    if (isFileSourceType(type) && name && !FILE_EXTENSIONS[type].some((ext) => name.endsWith(ext))) {
      this.fileForm = { ...this.fileForm, fileId: null, originalName: null, fileSize: null };
    }
  }

  get sourceTypeLabel(): string {
    return DATA_MAPPER_SOURCE_TYPES.find((t) => t.type === this.sourceType)?.label ?? this.sourceType;
  }

  /**
   * The forms are frozen while a call is in flight (the screen disables them
   * too): a connect to server A must not land after the form says server B
   * (Codex r2).
   */
  setFormField<K extends keyof DataMapperMssqlForm>(field: K, value: DataMapperMssqlForm[K]) {
    if (this.busy) return;
    this.form = { ...this.form, [field]: value };
    this.testResult = null;
  }

  setFileField<K extends keyof DataMapperFileForm>(field: K, value: DataMapperFileForm[K]) {
    if (this.busy) return;
    this.fileForm = { ...this.fileForm, [field]: value };
    this.testResult = null;
  }

  setRestField<K extends keyof DataMapperRestForm>(field: K, value: DataMapperRestForm[K]) {
    if (this.busy) return;
    // The form already sends the URL without its credential parts; this holds the line for any other caller.
    const next = field === "url" ? sanitizeRestUrl(String(value)).url : value;
    this.restForm = { ...this.restForm, [field]: next };
    this.testResult = null;
  }

  setSaveScope(scope: DataMapperScope) {
    this.saveScope = scope;
  }

  /** Fill the form from a saved config. A secret is never in one; the person types it. */
  loadConfig(id: string | null) {
    if (this.busy) return;
    this.error = null;
    this.notice = null;
    this.testResult = null;
    this.droppedSecrets = [];
    if (!id) {
      this.selectedConfigId = null;
      this.selectedConfigUpdatedAt = null;
      if (this.sourceType === "mssql") this.form = { ...EMPTY_FORM };
      else if (this.sourceType === "rest-api") this.restForm = { ...EMPTY_REST_FORM };
      else this.fileForm = { ...EMPTY_FILE_FORM };
      return;
    }
    const row = this.savedConfigs.find((c) => sameId(c.id, id));
    const type = row ? savedType(row) : null;
    if (!row || !type) return;
    const c = row.config;
    this.selectedConfigId = row.id;
    this.selectedConfigUpdatedAt = row.updatedAt;
    this.sourceType = type;
    this.saveScope = row.scope;
    if (type === "mssql") {
      this.form = {
        name: row.name,
        server: str(c.server),
        port: str(c.port),
        database: str(c.database),
        authType: c.authType === "windows" || c.authType === "integrated" ? c.authType : "sql",
        username: str(c.username),
        domain: str(c.domain),
        encrypt: c.encrypt !== false,
        trustServerCertificate: c.trustServerCertificate === true,
      };
    } else if (type === "rest-api") {
      const auth = (c.auth ?? {}) as Record<string, unknown>;
      const p = (c.pagination ?? {}) as Record<string, unknown>;
      const authType = ["bearer", "basic", "api-key"].includes(str(auth.type))
        ? (auth.type as DataMapperRestForm["authType"])
        : "none";
      const paginationType = ["offset", "page", "cursor"].includes(str(p.type))
        ? (p.type as DataMapperRestForm["paginationType"])
        : "none";
      this.restForm = {
        name: row.name,
        url: str(c.url),
        method: c.method === "POST" ? "POST" : "GET",
        authType,
        username: str(auth.username),
        apiKeyHeader: str(auth.headerName),
        responsePath: str(c.responsePath),
        paginationType,
        pageSize: str(p.pageSize) || EMPTY_REST_FORM.pageSize,
        maxPages: str(p.maxPages) || EMPTY_REST_FORM.maxPages,
        limitParam: str(p.limitParam),
        offsetParam: str(p.offsetParam),
        pageParam: str(p.pageParam),
        cursorParam: str(p.cursorParam),
        cursorPath: str(p.cursorPath),
      };
    } else {
      this.uploadError = null;
      this.fileForm = {
        name: row.name,
        fileId: str(c.fileId) || null,
        originalName: str(c.originalName) || null,
        fileSize: null,
        delimiter: str(c.delimiter) || EMPTY_FILE_FORM.delimiter,
        hasHeader: c.hasHeader !== false,
        encoding: str(c.encoding) || EMPTY_FILE_FORM.encoding,
        rootPath: str(c.rootPath),
        sheets: Array.isArray(c.sheets) ? c.sheets.map(str).filter(Boolean).join(", ") : "",
        tableName: str(c.tableName),
      };
    }
  }

  /**
   * The chosen form as a config body, WITHOUT any secret. A Windows-auth MSSQL
   * config carries its domain, dialed and saved alike (it is not a secret: the
   * API keeps it like the user name). A file config names its upload's
   * original name only when saved (the schema requires it).
   */
  private configBody(forSave = false): Record<string, unknown> {
    if (isFileSourceType(this.sourceType)) {
      const f = this.fileForm;
      const body: Record<string, unknown> = { type: this.sourceType, fileId: f.fileId };
      if (forSave) body.originalName = f.originalName ?? "upload";
      if (f.name.trim()) body.name = f.name.trim();
      if (this.sourceType === "csv") {
        body.delimiter = f.delimiter || ",";
        body.hasHeader = f.hasHeader;
        if (f.encoding.trim()) body.encoding = f.encoding.trim();
      }
      if (this.sourceType === "json" && f.rootPath.trim()) body.rootPath = f.rootPath.trim();
      if (this.sourceType === "excel") {
        const sheets = f.sheets
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        if (sheets.length) body.sheets = sheets;
      }
      if (f.tableName.trim()) body.tableName = f.tableName.trim();
      return body;
    }
    if (this.sourceType === "rest-api") {
      const r = this.restForm;
      const auth: Record<string, unknown> = { type: r.authType };
      if (r.authType === "basic" && r.username.trim()) auth.username = r.username.trim();
      if (r.authType === "api-key" && r.apiKeyHeader.trim()) auth.headerName = r.apiKeyHeader.trim();
      const body: Record<string, unknown> = { type: "rest-api", url: r.url.trim(), method: r.method, auth };
      if (r.name.trim()) body.name = r.name.trim();
      if (r.responsePath.trim()) body.responsePath = r.responsePath.trim();
      if (r.paginationType !== "none") {
        const pagination: Record<string, unknown> = { type: r.paginationType };
        const fields = {
          pageSize: positiveInt(r.pageSize),
          maxPages: positiveInt(r.maxPages),
          limitParam: trimmed(r.limitParam),
          offsetParam: r.paginationType === "offset" ? trimmed(r.offsetParam) : undefined,
          pageParam: r.paginationType === "page" ? trimmed(r.pageParam) : undefined,
          cursorParam: r.paginationType === "cursor" ? trimmed(r.cursorParam) : undefined,
          cursorPath: r.paginationType === "cursor" ? trimmed(r.cursorPath) : undefined,
        };
        for (const [k, v] of Object.entries(fields)) if (v !== undefined) pagination[k] = v;
        body.pagination = pagination;
      }
      return body;
    }
    const f = this.form;
    const body: Record<string, unknown> = {
      type: "mssql",
      server: f.server.trim(),
      database: f.database.trim(),
      authType: f.authType,
      encrypt: f.encrypt,
      trustServerCertificate: f.trustServerCertificate,
    };
    if (f.name.trim()) body.name = f.name.trim();
    // Windows login signs in as the connector's Windows account: no user name goes with it.
    if (f.authType !== "integrated" && f.username.trim()) body.username = f.username.trim();
    if (f.authType === "windows" && f.domain.trim()) body.domain = f.domain.trim();
    const port = Number(f.port);
    if (f.port.trim() !== "" && Number.isInteger(port)) body.port = port;
    return body;
  }

  /** The name a save gives the config when none was typed. */
  private defaultName(): string {
    if (isFileSourceType(this.sourceType)) return this.fileForm.name.trim() || this.fileForm.originalName || "Uploaded file";
    if (this.sourceType === "rest-api") return this.restForm.name.trim() || hostOf(this.restForm.url) || "REST API";
    return this.form.name.trim() || this.form.database.trim();
  }

  /**
   * The form's SQL Server as a dial config, WITHOUT a password: the Restore
   * screen dials the same server the person filled in here. Null when the form
   * was filled under another profile than the open one (Codex r1): the form is
   * only reset when this page is entered, so it may still name the last
   * profile's server.
   */
  get serverConfig(): Record<string, unknown> | null {
    if (!this.profileId || !sameId(this.formProfileId, this.profileId)) return null;
    return this.configBody();
  }

  /** Start an operation: one at a time, and its answer counts only while `opEpoch` holds. */
  private begin(kind: Busy): number {
    this.busy = kind;
    this.error = null;
    this.testResult = null;
    if (kind === "save") {
      this.notice = null;
      this.droppedSecrets = [];
    }
    return this.opEpoch;
  }

  /** Settle an operation's state, unless the route was left or the profile changed meanwhile. */
  private settle(epoch: number, apply: () => void) {
    runInAction(() => {
      if (epoch !== this.opEpoch) return;
      apply();
      this.busy = null;
    });
  }

  // ---- Files -----------------------------------------------------------------

  /**
   * Upload a file for the chosen file type. The type and size are checked
   * before a byte is sent; the answer's document id becomes the form's fileId.
   */
  async uploadFile(file: File): Promise<boolean> {
    const type = this.sourceType;
    if (!isFileSourceType(type) || this.busy) return false;
    const refused = checkSourceFile(file, type);
    if (refused) {
      this.uploadError = refused;
      return false;
    }
    const epoch = this.begin("upload");
    this.uploadError = null;
    this.uploadProgress = 0;
    // A raw XMLHttpRequest the session guard never sees. reset() aborts it,
    // but the epoch moves before the reset runs: the session is asked too.
    const ended = sessionMark(this.rootStore);
    let handle: UploadHandle | null = null;
    try {
      const { env } = await import("../../../config/environment");
      // The route was left (or the profile changed) while the import loaded: start nothing (Codex r1).
      // Nor after a sign-out: the token read next would be the next person's.
      if (epoch !== this.opEpoch || ended()) return false;
      const token =
        (this.rootStore as unknown as { authStore?: { token?: string | null } }).authStore?.token ?? null;
      handle = uploadSourceFile(file, {
        apiUrl: env.apiUrl,
        token,
        onProgress: (percent) =>
          runInAction(() => {
            if (epoch === this.opEpoch && !ended()) this.uploadProgress = percent;
          }),
      });
      this.upload = handle;
      const doc = await handle.done;
      if (ended()) return false;
      this.settle(epoch, () => {
        this.fileForm = {
          ...this.fileForm,
          fileId: doc.id,
          originalName: doc.originalFileName,
          fileSize: doc.fileSize,
          tableName: "",
        };
        // A new upload is a new source: it is not the saved config any more.
        this.selectedConfigId = null;
        this.selectedConfigUpdatedAt = null;
        this.uploadProgress = null;
      });
      return true;
    } catch (err) {
      if (isSessionEnded(err) || ended()) return false;
      this.settle(epoch, () => {
        this.uploadError = err instanceof Error && err.message ? err.message : "The file could not be uploaded.";
        this.uploadProgress = null;
      });
      return false;
    } finally {
      // Only our own handle: a reset may have cleared it and a newer upload taken the slot.
      if (handle && this.upload === handle) this.upload = null;
    }
  }

  /** Forget the uploaded file (the form only: the upload itself stays the uploader's). */
  removeFile() {
    if (this.busy) return;
    this.fileForm = { ...this.fileForm, fileId: null, originalName: null, fileSize: null };
    this.selectedConfigId = null;
    this.selectedConfigUpdatedAt = null;
    this.uploadError = null;
    this.testResult = null;
  }

  clearUploadError() {
    this.uploadError = null;
  }

  // ---- Dials -----------------------------------------------------------------
  // test / connect / listDatabases / saveConfig take a secret: they are not
  // MobX actions (see the constructor), and the secret goes only into the call body.

  private get testSentence(): (n: number) => string {
    const plural = (n: number) => `${n} table${n === 1 ? "" : "s"}`;
    if (isFileSourceType(this.sourceType)) return (n) => `HQ can read the file: ${plural(n)} found.`;
    if (this.sourceType === "rest-api") return (n) => `The API answered: ${plural(n)} found.`;
    return (n) => `The connection works: ${plural(n)} visible.`;
  }

  /** Test the source (opens and closes it; this tab's source is unchanged). */
  async test(secret: DialSecret = ""): Promise<boolean> {
    const service = dataPlaneService(this.rootStore, "data-mapper-data-source");
    const query = this.selection?.dataPlaneQuery;
    if (!service || !this.canDial || !this.selection?.hasProfile || !query || this.busy) return false;
    const config = this.configBody();
    const type = this.sourceType;
    const sentence = this.testSentence;
    const epoch = this.begin("test");
    try {
      const answer = (await service.create(dialRequest("test", query, config, secret))) as {
        tableCount?: number;
      } | null;
      const n = answer?.tableCount ?? 0;
      this.settle(epoch, () => {
        this.testResult = sentence(n);
      });
      return true;
    } catch (err) {
      if (isSessionEnded(err)) return false;
      this.settle(epoch, () => {
        this.error = dataSourceErrorMessage(err, "The test failed.", type);
      });
      return false;
    }
  }

  /**
   * Busy with something other than connect / disconnect (a test, a save, an
   * upload): those two queue behind each other, but not behind these.
   */
  get busyOtherThanDial(): boolean {
    return this.busy !== null && this.busy !== "connect" && this.busy !== "disconnect";
  }

  /**
   * Open this tab's source. On success the Browse screen can read it. Asked
   * while a connect or disconnect is in flight, it waits for it; asked again
   * before it starts, only the latest waiting call runs (false when skipped).
   */
  async connect(secret: DialSecret = ""): Promise<boolean> {
    const service = dataPlaneService(this.rootStore, "data-mapper-data-source");
    const query = this.selection?.dataPlaneQuery;
    if (!service || !this.canDial || !this.selection?.hasProfile || !query || this.busyOtherThanDial) return false;
    // The form as it is now: the intent is what was on screen when Connect was clicked.
    const config = this.configBody();
    const type = this.sourceType;
    const outcome = await this.intents.run(async () => {
      const epoch = this.begin("connect");
      try {
        const answer = (await service.create(dialRequest("connect", query, config, secret))) as {
          connected?: boolean;
          name?: string | null;
        } | null;
        const connected = answer?.connected === true;
        this.settle(epoch, () => {
          this.connected = connected;
          this.connectedName = connected ? (answer?.name ?? null) : null;
          this.statusLoaded = true;
        });
        return connected;
      } catch (err) {
        if (isSessionEnded(err)) return false;
        this.settle(epoch, () => {
          this.error = dataSourceErrorMessage(err, "The connection failed.", type);
        });
        return false;
      }
    });
    return outcome.ran ? outcome.value : false;
  }

  /** Close this tab's source. Serialised with connect, latest intent wins. */
  async disconnect(): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-data-source");
    const query = this.selection?.dataPlaneQuery;
    if (!service || !this.selection?.hasProfile || !query || this.busyOtherThanDial) return;
    await this.intents.run(async () => {
      const epoch = this.begin("disconnect");
      try {
        await service.create({ action: "disconnect", ...query });
        this.settle(epoch, () => {
          this.connected = false;
          this.connectedName = null;
        });
      } catch (err) {
        if (isSessionEnded(err)) return;
        this.settle(epoch, () => {
          this.error = dataSourceErrorMessage(err, "The source could not be closed.");
        });
      }
    });
  }

  /**
   * Admin only: list the server's databases (names). The API refuses anyone
   * else, and pages the list by bytes, so the cursor is followed to the end.
   */
  async listDatabases(password: string): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-data-source");
    const query = this.selection?.dataPlaneQuery;
    if (!service || !this.isAdmin || this.sourceType !== "mssql" || !this.form.server.trim() || !query || this.busy)
      return;
    const config = this.configBody();
    const epoch = this.begin("databases");
    try {
      const names: string[] = [];
      let skip: number | null = 0;
      // shortcut: each page is a fresh dial; at most MAX_DATABASE_PAGES of them (names are short).
      for (let page = 0; skip !== null && page < MAX_DATABASE_PAGES; page++) {
        const answer = (await service.create({
          ...dialRequest("list-databases", query, config, password),
          $skip: skip,
        })) as { rows?: Array<{ name?: unknown }>; nextCursor?: number | null } | null;
        names.push(...(answer?.rows ?? []).map((r) => str(r.name)).filter(Boolean));
        skip = typeof answer?.nextCursor === "number" && answer.nextCursor > skip ? answer.nextCursor : null;
      }
      // A list cut at the ceiling says so rather than pass for the whole list (Codex r2).
      const cut = skip !== null;
      this.settle(epoch, () => {
        this.databases = names;
        if (cut) {
          this.error =
            `This server has more databases than HQ lists here; the first ${names.length} are shown. ` +
            "Type the database name if yours is not among them.";
        }
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      this.settle(epoch, () => {
        this.error = dataSourceErrorMessage(err, "The databases could not be listed.");
      });
    }
  }

  // ---- Saved configs ---------------------------------------------------------

  /**
   * Save the form as a config: a patch of the loaded one (with expectUpdatedAt,
   * so a concurrent save is a 409) or, with `asNew` or nothing loaded, a new
   * one in `saveScope`. The API keeps no secret and answers `droppedSecrets`,
   * the fields it dropped, which the screen shows.
   *
   * A REST save carries the secrets the person typed (`restSecrets`) so the API
   * can say which it dropped, and therefore goes through the RAW client: a
   * ServiceStore call is a MobX action whose arguments spy() reports. MSSQL and
   * file saves carry no secret and go through the ServiceStore.
   */
  async saveConfig(asNew = false, restSecrets?: DataMapperRestSecrets): Promise<boolean> {
    const selection = this.selection;
    const type = this.sourceType;
    const isRest = type === "rest-api";
    const service = isRest ? rawSourceConfigsService(this.rootStore) : this.configsService;
    if (!service || !selection?.hasProfile || !this.canDial || this.busy) return false;
    const base = this.configBody(true);
    const name = this.defaultName();
    const editingId = asNew ? null : this.selectedConfigId;
    const expectUpdatedAt = this.selectedConfigUpdatedAt;
    const scope = this.saveScope;
    const selectionQuery = { ...selection.selectionQuery };
    const epoch = this.begin("save");
    try {
      const config = isRest && restSecrets ? restConfigWithSecrets(base, restSecrets) : base;
      const saved = (
        editingId
          ? await service.patch(
              editingId,
              { name, config, ...(expectUpdatedAt ? { expectUpdatedAt } : {}) } as never,
              { query: selectionQuery } as never
            )
          : await service.create({ name, config, scope, ...selectionQuery } as never)
      ) as DataMapperSourceConfig | null;
      // The raw client wrote nothing to the cache: refresh it so the picker lists the row.
      if (isRest && epoch === this.opEpoch) {
        await this.configsService?.find({ query: selectionQuery }).catch(() => undefined);
      }
      const dropped = Array.isArray(saved?.droppedSecrets) ? saved.droppedSecrets.map(String) : [];
      this.settle(epoch, () => {
        if (saved?.id) {
          this.selectedConfigId = saved.id;
          this.selectedConfigUpdatedAt = saved.updatedAt;
        }
        this.droppedSecrets = dropped;
        this.notice =
          type === "mssql"
            ? `Saved "${name}". The password is not saved; type it each time you connect.`
            : isRest
              ? `Saved "${name}". Credentials are not saved; type them each time you connect.`
              : `Saved "${name}". Only you (and administrators) can open its uploaded file.`;
      });
      return true;
    } catch (err) {
      if (isSessionEnded(err)) return false;
      if (isRecordChanged(err)) {
        this.settle(epoch, () => {
          this.notice = sourceConfigChangedNotice(name);
          this.selectedConfigId = null;
          this.selectedConfigUpdatedAt = null;
        });
        if (epoch === this.opEpoch) await this.load();
        return false;
      }
      this.settle(epoch, () => {
        this.error = loadErrorMessage(err, "The connection could not be saved.");
      });
      return false;
    }
  }

  clearError() {
    this.error = null;
  }

  clearNotice() {
    this.notice = null;
    this.droppedSecrets = [];
  }

  goToBrowse() {
    void this.rootStore.routerStore?.goTo(DATA_MAPPER_BROWSE_ROUTE);
  }

  /** The explorer: schema, profiles, AI analysis and reports of the open source. */
  goToExplore() {
    void this.rootStore.routerStore?.goTo(DATA_MAPPER_EXPLORE_ROUTE);
  }

  /** Route exit: drop messages, the admin database list and any running upload. The forms stay. */
  reset() {
    this.stopConnectorWatch();
    this.connectorError = null;
    this.loadSeq++;
    this.opEpoch++;
    this.intents.supersede();
    this.upload?.abort();
    this.upload = null;
    this.uploadProgress = null;
    this.uploadError = null;
    this.busy = null;
    this.error = null;
    this.notice = null;
    this.droppedSecrets = [];
    this.testResult = null;
    this.configsError = null;
    this.configsLoading = false;
    this.databases = [];
    this.statusLoaded = false;
  }

  /**
   * Everything reset() keeps across this wizard's route changes goes too: on
   * sign-out it was the previous person's. Called by RootStore.purgeUserData,
   * which prefers this to reset().
   */
  resetForSignOut() {
    this.reset();
    this.sourceType = "mssql";
    this.form = { ...EMPTY_FORM };
    this.fileForm = { ...EMPTY_FILE_FORM };
    this.restForm = { ...EMPTY_REST_FORM };
    this.selectedConfigId = null;
    this.selectedConfigUpdatedAt = null;
    this.saveScope = "profile";
    this.formProfileId = null;
    this.connected = false;
    this.connectedName = null;
    this.connector = null;
    this.connectorDownloading = null;
  }
}

export default DataMapperSourceStore;
